<template>
    <div class="p-2 h-full flex flex-col gap-2">
        <div class="flex gap-2">
            <UButton @click="() => onSearchUpdated(searchParams)">Søk</UButton>
            <transaction-filter enable-date-filter @update="onSearchUpdated"/>
        </div>
        <ag-grid-vue
            :grid-options="gridOptions"
            :row-data="mappedRows"
            :column-defs="colDefs"
            :theme="agTheme"
            class="h-full grow"
        />
    </div>
</template>

<script setup lang="ts">

import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {computed, inject, Ref, ref} from 'vue'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {get} from '@/services/infrastructure/api.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {GridOptions} from 'ag-grid-community'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import {formatCurrency, formattedDate} from '@/services/formatter.service'

const searchParams = ref<TransactionSearchParams>(defaultSearchParams())
const TRANSACTIONS_PATH = '/transactions'

const TRANSACTION_LINE_PATH = '/-/transaction-lines'
const rowData = ref<TransactionLine[]>([])
const panelId: Ref<number> | undefined = inject('panelId')

const mappedRows = computed(() => {
    return rowData.value.map((tl: TransactionLine) => {
        return {
            ...tl,
            subAccount: tl.subAccountUuid && findSubAccountByUuid(tl.subAccountUuid)
        }
    })
})

const searchTransactionLines = async (): Promise<TransactionLine[]> => {
    try {
        return await get<TransactionLine[]>(TRANSACTIONS_PATH + TRANSACTION_LINE_PATH, {
            ...searchParams,
            startRow: 0,
            endRow: 10_000
        }) ?? []
    } catch (e: any) {
        notifyError({title: 'Feilet', description: {descriptionMessage: 'Error in searchTransactionLine', error: e}})
        return []
    }
}

const onSearchUpdated = async (newSearchParams: TransactionSearchParams) => {
    searchParams.value = newSearchParams
    rowData.value = []
    rowData.value = await searchTransactionLines()
}


const gridOptions: GridOptions = {
    enableCharts: true,
    sideBar: ['columns', 'filters-new'],
    cellSelection: true,
    enableFilterHandlers: true,
    autoGroupColumnDef: {
        minWidth: 300
    },
    defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enableRowGroup: true,
        enablePivot: true,
        filter: true
    },
    context: {
        panelId
    }
}

const colDefs = computed(() => {
    return [
        {
            field: 'date',
            cellRenderer: ({value}: { value: string }) => formattedDate(value)
        },
        {field: 'accountNo'},
        {field: 'subAccount.name', headerName: 'Navn'},
        {field: 'subAccount.address.postalCode', headerName: 'Postnummer'},
        {field: 'invoiceNo'},
        {field: 'description'},
        {
            field: 'amount',
            cellClass: 'text-right',
            cellRenderer: ({value}: { value: number }) => formatCurrency(value)
        }
    ]
})

</script>

<style scoped>

</style>